/**
 * Per-key single-flight guard for idempotent, read-only async work.
 *
 * Origin (2026-09-29): the web wake-fix trace showed the dashboard's
 * verification check starting TWICE 13ms apart during one page load. Cause:
 * React.StrictMode (dev only, src/index.js) double-invokes effects, and the
 * dashboard's mount effect launches checkVerification each time. Production
 * builds do not double-invoke effects, and the `user` prop driving that
 * effect is the stable Firebase UserImpl reference (one change per real
 * sign-in/out), so the duplication is a development artifact — but rather
 * than rely on that reasoning, callers use this guard so two concurrent
 * starts for the same key are structurally impossible on any build.
 *
 * Scope constraints (approved 2026-09-29 — keep it exactly this small):
 * - Deduplicates ONLY concurrent copies of the same read-only load for the
 *   same key (signed-in uid) within the component instance that owns the
 *   guard. Create one per instance via useRef; unmount discards it with the
 *   instance, so nothing leaks across screens, sessions, or users.
 * - NOT a cache: it stores no results and remembers nothing once a run
 *   settles. The moment the in-flight run finishes — success OR failure —
 *   the guard is empty and the next call runs for real.
 * - Never suppresses a real refresh: a later mount, a manual retry, or a
 *   different uid always executes. Only a start that overlaps an identical
 *   in-flight start is dropped (not queued): {started:false}.
 */
export function createSingleFlight() {
  const inFlight = new Set();
  return async function runExclusive(key, fn) {
    if (inFlight.has(key)) return { started: false };
    inFlight.add(key);
    try {
      await fn();
      return { started: true };
    } finally {
      inFlight.delete(key);
    }
  };
}
